import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Popover,
  IconButton,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Stack,
  CircularProgress,
} from "@mui/material";
import RemoveRedEyeSharpIcon from "@mui/icons-material/RemoveRedEyeSharp";
import { NotificationIcon } from "../icons";
import { useNavigate } from "react-router";
import { ROUTE_PATHS } from "../../routes/paths";
import { APP_COLORS, ENUM_USER_ROLE } from "../../enums";
import { getUpcomingMeetings } from "../../api/projects";
import { confirmMeetingAttendance, getInvitationById } from "../../api/invitations";
import { useQueryWithDispatch } from "../../hooks/useQueryWithDispatch";
import { useAppDispatch, useAppSelector } from "../../store";
import { deleteNotifications } from "../../api/profile";
import { MeetingAttendanceNotificationItem } from "./meeting-attendance";
import { MeetingRescheduleDrawer } from "../../screens/project-details/ambassador-slot-selection/meeting-reschedule-drawer";
import { invalidateMeetingsOverviewQueries } from "../../screens/my-projects/utils";
import {
  getUpcomingMeetingDisplayName,
  getUpcomingMeetingInvitationId,
  getUpcomingMeetingTimeLabel,
  getUnmarkedUpcomingMeetings,
  UpcomingMeetingNotificationItem,
} from "./utils";

const NotificationPopover = ({ notifications = [], iconButtonSx = {} }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rescheduleDrawerOpen, setRescheduleDrawerOpen] = useState(false);
  const [selectedUpcomingMeeting, setSelectedUpcomingMeeting] =
    useState<UpcomingMeetingNotificationItem | null>(null);
  const userData = useAppSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const browserTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );
  const isAmbassador = userData?.role === ENUM_USER_ROLE.AMBASSADOR;

  const {
    data: upcomingMeetingsResponse,
    isPending: upcomingMeetingsLoading,
    refetch: refetchUpcomingMeetings,
  } = useQueryWithDispatch({
    queryKey: ["notifications", "upcoming-meetings", browserTimezone],
    thunkAction: getUpcomingMeetings,
    variables: { timezone: browserTimezone },
    enabled: isAmbassador,
  });

  const upcomingMeetings = upcomingMeetingsResponse?.data ?? [];
  const unmarkedUpcomingMeetings = useMemo(
    () => getUnmarkedUpcomingMeetings(upcomingMeetings),
    [upcomingMeetings]
  );

  const mappedNotifications = useMemo(
    () =>
      notifications
        ?.filter((n) => n.count > 0)
        .map((n) => ({
          title: "Views",
          description: `You have ${n.count} new views on your profile`,
          icon: <RemoveRedEyeSharpIcon style={{ color: "#00A86B" }} />,
          iconBgColor: "#d6e5ec",
          onClick: () => {
            setAnchorEl(null);
            userData?.role === ENUM_USER_ROLE.VENDOR
              ? navigate({
                  pathname: ROUTE_PATHS?.MY_PROFILE,
                  search: "?type=profile",
                })
              : navigate(ROUTE_PATHS?.MY_PROFILE);
          },
        })) ?? [],
    [notifications, navigate, userData?.role]
  );

  const hasUpcomingMeetings = upcomingMeetings.length > 0;
  const hasUserNotifications = mappedNotifications.length > 0;
  const badgeCount =
    unmarkedUpcomingMeetings.length +
    (hasUserNotifications ? mappedNotifications.length : 0);

  const handleClick = async (event) => {
    setAnchorEl(event.currentTarget);

    if (isAmbassador) {
      refetchUpcomingMeetings();
    }

    if (mappedNotifications?.length > 0) {
      dispatch(deleteNotifications());
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRescheduleClick = async (meeting: UpcomingMeetingNotificationItem) => {
    const invitationId = getUpcomingMeetingInvitationId(meeting);
    if (!invitationId) return;

    const response = await dispatch(getInvitationById(invitationId));
    if (response?.success) {
      setSelectedUpcomingMeeting(response.data);
      setRescheduleDrawerOpen(true);
      handleClose();
    }
  };

  const handleCloseRescheduleDrawer = () => {
    setRescheduleDrawerOpen(false);
    setSelectedUpcomingMeeting(null);
  };

  const handleRescheduleSuccess = () => {
    invalidateMeetingsOverviewQueries(queryClient, userData?.role);
    refetchUpcomingMeetings();
  };

  const handleConfirmAttendance = async (
    meeting: UpcomingMeetingNotificationItem
  ) => {
    const invitationId = getUpcomingMeetingInvitationId(meeting);
    if (!invitationId) return;

    const response = await dispatch(confirmMeetingAttendance(invitationId));
    if (response?.success) {
      handleRescheduleSuccess();
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "app-notification-popover" : undefined;

  return (
    <div>
      <Badge
        badgeContent={badgeCount > 9 ? "9+" : badgeCount}
        color="error"
        invisible={badgeCount <= 0}
        overlap="circular"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiBadge-badge": {
            height: 18,
            minWidth: 18,
            fontSize: 10,
            fontWeight: 700,
            px: 0.5,
            border: `2px solid ${APP_COLORS.primaryDark}`,
            boxSizing: "border-box",
          },
        }}
      >
        <IconButton
          aria-describedby={id}
          onClick={handleClick}
          sx={iconButtonSx}
        >
          <NotificationIcon />
        </IconButton>
      </Badge>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            backgroundColor: "#f3f3fd",
            borderRadius: "10px",
          },
        }}
      >
        <Box width={420} p={2}>
          {isAmbassador && upcomingMeetingsLoading ? (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <>
              {hasUpcomingMeetings && (
                <Stack gap={1} mb={hasUserNotifications ? 2 : 0}>
                  {upcomingMeetings.map((meeting) => (
                    <MeetingAttendanceNotificationItem
                      key={getUpcomingMeetingInvitationId(meeting) ?? meeting}
                      vendorName={getUpcomingMeetingDisplayName(meeting)}
                      meetingTimeLabel={getUpcomingMeetingTimeLabel(
                        meeting,
                        browserTimezone
                      )}
                      isAmbassadorAttend={Boolean(meeting?.isAmbassadorAttend)}
                      onConfirm={() => handleConfirmAttendance(meeting)}
                      onReschedule={() => handleRescheduleClick(meeting)}
                    />
                  ))}
                </Stack>
              )}

              {hasUserNotifications && (
                <>
                  <Typography
                    variant="h6"
                    fontSize={13}
                    color="#000"
                    textAlign={hasUpcomingMeetings ? "left" : "left"}
                    mb={1}
                  >
                    New
                  </Typography>
                  <List disablePadding>
                    {mappedNotifications.map((notification) => (
                      <ListItem
                        key={notification.description}
                        sx={{
                          borderRadius: 2,
                          marginBottom: 1.5,
                          backgroundColor: "#e9eafc",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                          py: 2,
                          cursor: "pointer",
                        }}
                        onClick={notification.onClick}
                      >
                        <ListItemAvatar>
                          <Avatar
                            variant="square"
                            sx={{
                              backgroundColor: notification.iconBgColor,
                              mr: 2,
                            }}
                          >
                            {notification.icon}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle1"
                              fontSize={13}
                              fontWeight="bold"
                              color="#000"
                            >
                              {notification.title}
                            </Typography>
                          }
                          secondary={notification.description}
                        />
                        <Box ml="auto" display="flex" alignItems="center">
                          <Box
                            width={8}
                            height={8}
                            borderRadius="50%"
                            bgcolor="#0000FF"
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {!hasUpcomingMeetings && !hasUserNotifications && (
                <Typography
                  variant="h6"
                  fontSize={13}
                  color="#000"
                  textAlign="center"
                >
                  You have no new notifications
                </Typography>
              )}
            </>
          )}
        </Box>
      </Popover>
      <MeetingRescheduleDrawer
        open={rescheduleDrawerOpen}
        onClose={handleCloseRescheduleDrawer}
        invitationId={getUpcomingMeetingInvitationId(selectedUpcomingMeeting)}
        clientTimezoneValue={
          selectedUpcomingMeeting?.client?.timezone?.value
        }
        onSuccess={handleRescheduleSuccess}
        projectDetails={selectedUpcomingMeeting}
      />
    </div>
  );
};

export default NotificationPopover;
